import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Redirect, withRouter } from 'react-router-dom';

import { Wizard } from '@xengage/gw-portals-wizard-react';
import { withViewModelService} from '@xengage/gw-portals-viewmodel-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useTranslator } from '@jutro/locale';
import { LoadSaveService } from 'gw-capability-gateway-quoteandbind';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { Loader, useModal } from '@jutro/components';
import { MockUpUtil } from '@xengage/gw-portals-util-js';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { QuoteInfoComponentContext } from 'gw-capability-quoteandbind-common-react';
import { PECustomQuoteInfoComponent } from 'ml-capability-quoteandbind-common-react';
import { FormModalPopover, platformMessages as mlCommonMessages } from 'ml-components-platform-react';
import { MLTagManagerService, MLModalService } from 'ml-portals-services-react';
import { MLQuoteFlowUtil } from 'ml-portals-util-js';
import { MLMultiLobsDataHolderService, MLStateRolloutService } from 'ml-services-common-react';
import { MLAgentChoiceReportService } from 'gw-capability-gateway';
import { Submission } from 'gw-capability-quoteandbind';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import { ClauseService } from 'gw-capability-policycommon';
import { MLLandingPageProvider } from 'gw-capability-gateway-quoteandbind-common-react';

import wizardConfig from './config/pa-wizard-config.json5';
import crWizardConfig from './config/pa-wizard-comprater-config.json5';
import wizardStepToFieldMapping from './config/pa-wizard-step-to-field-mapping.json5';
import messages from './PEPAWizard.messages';

const PATH_TO_MOCK_DATA = 'quote.pa';
const MOCK_DATA_TO_SET = ['bindData.contactPhone'];
const MOCK_DATA_TO_REMOVE = ['bindData.contactPhone'];

const crWizardStepNames = ['compPaGroupAssociations','compPaPrefillResults', 'compPaDrivers', 'compPaVehicles', 'compPaValidateInfo', 'quoteCompRaterDetails', 'compPaVeracityCheckPostLanding', 'compPaVeracityCheckPostValidate'];

function PEPAWizard(props) {
    const modalApi = useModal();
    const { steps, title } = wizardConfig;
    const { steps: crSteps, title: crTitle} = crWizardConfig;
    const { authHeader } = useAuthentication();
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const [shouldSkipValidSteps, setShouldSkipValidSteps] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isCREligible, setIsCREligible] = useState(undefined);
    const [isGroupAccessEligible, setIsGroupAccessEligible] = useState(undefined);
    const { viewModelService, history, location } = props;

    const { showProgressDialog,hideProgressDialog } = MLModalService()
    const { showReportModalWithSubmissionVM } = MLAgentChoiceReportService();
    const { cancel } = MLQuoteFlowUtil();

    const previousStep = useRef(MLMultiLobsDataHolderService.getLastVisitedStep());

    const viewModelContext = {
        AccountEmailRequired: false,
        DriverEmailRequired: true,
        AccountDOBRequired: false
    };

    const translator = useTranslator();

    useEffect(() => {
        if (_.has(location, 'state.submission') && isGroupAccessEligible === undefined) {
            const { submission } = location.state;
            MLStateRolloutService.checkIsGroupEnabled(submission?.baseData?.policyAddress?.state, authHeader).then((isGroupEligible) => {
                isGroupEligible = submission?.producerCodeOfRecord_ML?.producerType_ML === "ia" ? false : isGroupEligible;
                const isStrategicPartner = localStorage.getItem('strategicPartnershipInd_Ext');

                if (isStrategicPartner?.toLowerCase() === 'y') {
                    setIsGroupAccessEligible(false)
                }
                else {
                    setIsGroupAccessEligible(isGroupEligible);
                }
            });
        }
    }, [authHeader, isGroupAccessEligible, location]);

    useEffect(() => {
        if (initialSubmission) {
            const isGCS = _.get(initialSubmission, 'producerCodeOfRecord_ML.producerType_ML.value.code') === 'gcs';

            MLStateRolloutService.checkIsCRWizardEligible(initialSubmission.quoteID.value, authHeader, isGCS).then((isCRWizardEligible) => {
                setIsCREligible(isCRWizardEligible);
            });
        }
    }, [initialSubmission, authHeader]);

    const updateInitialSubmission = useCallback((submission) => {
        setInitialSubmission(submission);
    }, [setInitialSubmission]);

    useEffect(() => {
        const visitedStep = MLMultiLobsDataHolderService.getLastVisitedStep();
        previousStep.current = visitedStep
    }, [MLMultiLobsDataHolderService.getLastVisitedStep()])

    useEffect(
        () => {
            if (!location.state) {
                history.push('/');
                return;
            }

            let submissionPromise;
            if (_.has(location, 'state.submission')) {
                const { submission, resumeFlow } = location.state;
                if (_.get(submission, 'baseData.sameMailingAddress_ML', 'undefined')) {
                    _.set(submission, 'baseData.sameMailingAddress_ML', true);
                }
                if (_.isUndefined(submission.baseData.priorAddressQuestion_Ext) ) {
                    _.set(submission, 'baseData.priorAddressQuestion_Ext', false);
                }
                if (resumeFlow) {
                    setShouldSkipValidSteps(true);
                }
                submissionPromise = Promise.resolve(submission);
            } else {
                const { quoteentry } = location.state;
                const { postalCode, quoteID } = quoteentry;
                const requestData = {
                    quoteID: quoteID,
                    postalCode: postalCode
                };
                submissionPromise = LoadSaveService.retrieveSubmission(requestData, authHeader);
            }

            submissionPromise.then((response) => {
                MockUpUtil.cleanUpMockedProperties(
                    response,
                    PATH_TO_MOCK_DATA,
                    ...MOCK_DATA_TO_REMOVE
                );

                ClausesUtil.getDependentClausesFromServer(
                    ClauseService,
                    _.get(response, 'quoteID'),
                    authHeader
                );

                const submission = viewModelService.create(
                    new Submission(response),
                    'pc',
                    'edge.capabilities.quote.submission.dto.QuoteDataDTO',
                    viewModelContext
                );
                setInitialSubmission(submission);
                setIsLoading(false);
            });
            return () => {
                MLTagManagerService.setModel();
            }
        },
        // Disabled so we don't rerun this function on every rerender
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleCancel = useCallback(
        (wizardProps) => {
            const { wizardSnapshot, wizardData, param: nextPath = '', currentStepIndex, steps: wizardSteps } = wizardProps;
            const skipModal = ['knockoutpage', 'logout', 'contactUs'];
            const lastPathName = nextPath.replace(/\/?.*\//g, '');
            const quoteSummaryRegex = /^\/accounts\/[0-9]+\/summary/;
            const isStatusBound = _.get(wizardSnapshot, 'baseData.periodStatus.value.code') === 'Bound';
            const lastVisitedStep = MLMultiLobsDataHolderService.getLastVisitedStep();
            const isStatusQuoted = _.get(wizardData, 'baseData.periodStatus.value.code') === 'Quoted';
            const isBundle = _.get(wizardData, 'isBundle_ML.value') === true;

            // Don't display modal service once the submission is bound or if
            // next path matches /quotes/<job number>/summary
            if (
                isStatusBound
                || skipModal.includes(lastPathName)
                || nextPath.match(quoteSummaryRegex)
                || hasErrorOccurred
                || currentStepIndex === wizardSteps.length - 1
            ) {
                return true;
            }

            // Allow transition to Bundle Cart, Account Summary, or Comp Rater Wizard
            if (nextPath.includes('/agent-bundle-cart') || nextPath.includes('/accounts') || nextPath.includes('/comp-wizard')) {
                return true;
            }

            if (nextPath.includes('/quoteentry')) {
                return true;
            }

            let modalMsg = mlCommonMessages.mlInfoWillBeSavedAsQuoteSubmission;
            if (!wizardData.quoteID.value) {
                modalMsg = mlCommonMessages.mlInfoWillNotBeSaved;
            } else if (!wizardData.quoteData.value) {
                modalMsg =mlCommonMessages.mlInfoSaveAsDraft;
            }

            return modalApi.showModal(
                <FormModalPopover
                    title={translator(mlCommonMessages.areYouSureYouWantToCancel)}
                    simpleMessage={translator(modalMsg)}
                    actionBtnLabel={commonMessages.yes}
                    cancelBtnLabel={mlCommonMessages.mlCancel}
                    closeSimpleMessage={false}
                />
                ).then(async() => {
                    setIsLoading(true);

                    try {
                        showProgressDialog(mlCommonMessages.saveSubmission);
                        await cancel(wizardData, lastVisitedStep, showReportModalWithSubmissionVM, authHeader);
                        hideProgressDialog();
                        history.push(nextPath);
                        return true;
                    } catch (error) {
                        hideProgressDialog();
                        history.push(nextPath);
                    };
                }, _.noop);
            },
            [authHeader, cancel, hasErrorOccurred, hideProgressDialog, history, modalApi, showProgressDialog, showReportModalWithSubmissionVM, translator]
        );

    const handleError = useCallback(
        (error) => {
            const isQuotePage = _.includes(_.toLower(_.get(error, 'gwInfo.method')), 'quote');
            const state = {
                pathname: '/contactUs',
                state: error
            };

            setHasErrorOccurred(true);
            return <Redirect push to={state} />
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleKnockout = useCallback(({ knockOutErrors }) => {
        history.push('/knockoutpage', { underwritingIssues: knockOutErrors });
    }, [history]);

    if (isLoading || isCREligible === undefined) {
        return <Loader loaded={!isLoading && isCREligible !== undefined} />;
    }

    if (!initialSubmission) {
        return null;
    }

    const submissionStatus = _.get(initialSubmission, 'baseData.status_ML.value');
    const isBindSubmission = !!_.get(initialSubmission, 'bindData.policyNumber.value') && (submissionStatus === 'Bound' || submissionStatus === 'Binding');
    const wizardLastVisitedStep = MLMultiLobsDataHolderService.getLastVisitedStep()
    const isFullQuote = !!wizardLastVisitedStep && !crWizardStepNames.find(stepName => stepName === wizardLastVisitedStep);
    const showCRSteps = _.get(initialSubmission, 'hvqQuoteID_ML.value') && !isFullQuote && isCREligible; 
    let filteredSteps = steps;
    let crFilteredSteps = crSteps;

    if (!showCRSteps && !isGroupAccessEligible) {
        // remove Group Affiliation screen from steps
        filteredSteps = steps.filter((step) => step.mlStepName !== 'paGroupAssociations');
    }

    if(showCRSteps && !isGroupAccessEligible) {
        crFilteredSteps = crSteps.filter((step) => step.mlStepName !== 'compPaGroupAssociations');
    }

    const stepsToShow = showCRSteps ? crFilteredSteps : filteredSteps;

    return (
        <ErrorBoundary onError={handleError}>
            <QuoteInfoComponentContext.Provider value={PECustomQuoteInfoComponent}>
                <MLLandingPageProvider updateWizardSubmission={updateInitialSubmission}>
                    <Wizard
                        key={`PEPAWizard-${showCRSteps}`}
                        initialSteps={isBindSubmission ? [stepsToShow[stepsToShow.length -1]] : stepsToShow}
                        wizardTitle={showCRSteps ? crTitle: title}
                        initialData={initialSubmission}
                        onCancel={handleCancel}
                        onKnockOut={handleKnockout}
                        skipCompletedSteps={shouldSkipValidSteps}
                        wizardStepToFieldMapping={wizardStepToFieldMapping}
                        onPreviousModalProps={{
                            title: commonMessages.wantToJump,
                            message: commonMessages.wantToJumpMessage,
                            status: 'warning',
                            icon: 'mi-error-outline',
                            confirmButtonText: commonMessages.yesModel,
                            cancelButtonText: commonMessages.cancelModel
                        }}
                    />
                </MLLandingPageProvider>
            </QuoteInfoComponentContext.Provider>
        </ErrorBoundary>
    );
}

PEPAWizard.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    location: PropTypes.shape({
        state: PropTypes.shape({
            quoteentry: PropTypes.shape({
                postalCode: PropTypes.string,
                quoteID: PropTypes.string
            })
        })
    }).isRequired
};

export default withRouter(withViewModelService(PEPAWizard));
